/**
 * @fileoverview Shared types and data hooks for the polymorphic record-link UI
 * @module components/jobs/phases/gather/summarization/shared-panels/linkShared
 *
 * Purpose:
 * - Drive `LinkedRecordsSection`, `LinkPicker`, and table link indicators with
 *   a polymorphic detail↔anchor model so the same UI works on every detail
 *   (security interest / loan / ATO-SRO) and every anchor (asset / creditor).
 *
 * Dependencies:
 * - `convex/jobs/summarization/recordLinks/{linkMutations,linkQueries}` —
 *   the polymorphic backend.
 *
 * Used by:
 * - LinkedRecordsSection.tsx
 * - LinkPicker.tsx
 * - SecondaryDetailPanel.tsx
 * - Every detail/anchor list panel (LinkIndicator counts)
 */

import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { BadgeColor } from "@/ui/primitives/badge";
import { AGENT_ICONS } from "../agentRegistry";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Detail-side record types — provide additional evidence for an anchor. */
export type DetailType = "security_interest" | "loan" | "ato_sro";

/** Anchor-side record types — the source-of-truth rows. */
export type AnchorType = "asset" | "creditor";

/**
 * Union of every record type the linking UI handles. Used by panel callbacks
 * (e.g. `onOpenLinked(type, id)`), the `SecondaryDetailPanel` dispatch, and
 * `RecordRef`. The literal `"creditor"` represents `liabilityEntries` rows —
 * the schema renamed the canonical anchor literal during the source-of-truth
 * refactor.
 */
export type RecordType = DetailType | AnchorType;

/**
 * Back-compat alias retained while we migrate call sites. Always equal to
 * `RecordType`; new code should prefer `RecordType`.
 *
 * @deprecated Use `RecordType` instead.
 */
export type LinkRecordType = RecordType;

/** Shape returned by `api.jobs.summarization.recordLinks.linkQueries.listLinksForJob`. */
export interface JobLink {
  _id: string;
  detailType: DetailType;
  detailId: string;
  /**
   * Anchor side. `asset` / `creditor` for the standard model; `loan` for the
   * Security-Interest ↔ Loan pairing, where the loan acts as the anchor.
   */
  anchorType: AnchorType | "loan";
  anchorId: string;
  rank: number;
  source: "agent_auto" | "agent_suggested_accepted" | "practitioner";
  createdBy: string;
  createdAt: number;
}

/** Shape returned by `api.jobs.summarization.recordLinks.linkQueries.listSuggestionsForJob`. */
export interface JobLinkSuggestion {
  _id: string;
  detailType: DetailType;
  detailId: string;
  anchorType: AnchorType;
  anchorId: string;
  confidence: "weak";
  reason: string;
  createdAt: number;
}

/** A record identified for display in a linked-records list or picker. */
export interface RecordRef {
  type: RecordType;
  id: string;
  title: string;
  subtitle?: string;
  /**
   * Headline amount for the record, shown as a value badge in linked-records
   * lists so the netting calc is legible: asset = selected valuation, creditor
   * = amount owed, security interest = owedAmount, loan = outstanding balance,
   * ATO/SRO = running balance. `undefined` when the record has no figure yet.
   */
  amount?: number;
}

/** Identifies the record a "Linked" section is rendered for. */
export interface LinkAnchor {
  type: RecordType;
  id: string;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const RECORD_TYPE_LABEL: Record<RecordType, string> = {
  security_interest: "Security interest",
  asset: "Asset",
  creditor: "Creditor",
  loan: "Loan",
  ato_sro: "ATO/SRO",
};

/**
 * @deprecated Use `RECORD_TYPE_LABEL` instead. Retained for back-compat with
 * call sites still importing `ANCHOR_TYPE_LABEL` from this module.
 */
export const ANCHOR_TYPE_LABEL = RECORD_TYPE_LABEL;

/**
 * Per-record-type icon, reusing the very same lucide icons the Data agents
 * show on their cards/sidebar (see `agentRegistry.ts`) so a linked Loan in a
 * panel reads with the same glyph as the Loans agent. Keyed off the agent
 * manifest ids to stay in lock-step with the registry.
 */
export const RECORD_TYPE_ICON: Record<RecordType, LucideIcon> = {
  asset: AGENT_ICONS["sum_asset_list"],
  creditor: AGENT_ICONS["sum_general_liabilities"],
  security_interest: AGENT_ICONS["sum_security_interests"],
  loan: AGENT_ICONS["sum_loans"],
  ato_sro: AGENT_ICONS["sum_ato_sro"],
};

/**
 * Representative colour per record type for the linked-records "calc" UI.
 * The netting reads as money-in vs money-out, so the colours are semantic, not
 * decorative: an asset is value recovered (green); everything the estate owes —
 * creditors, loans, ATO/SRO, and the owed side of a security interest — is a
 * debt (red). The type badge and amount badge of a linked record share this
 * colour so a row's role is legible at a glance.
 */
export const RECORD_TYPE_BADGE_COLOR: Record<RecordType, BadgeColor> = {
  asset: "emerald",
  creditor: "red",
  loan: "red",
  ato_sro: "red",
  security_interest: "red",
};

export const DETAIL_TYPES: ReadonlyArray<DetailType> = [
  "security_interest",
  "loan",
  "ato_sro",
];

export const ANCHOR_TYPES: ReadonlyArray<AnchorType> = ["asset", "creditor"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function isAnchorType(type: RecordType): type is AnchorType {
  return type === "asset" || type === "creditor";
}

export function isDetailType(type: RecordType): type is DetailType {
  return type === "security_interest" || type === "loan" || type === "ato_sro";
}

/**
 * Links touching the given record id, regardless of which side.
 *
 * A record can sit on either side of a link — loans are the *detail* for
 * asset/creditor links but the *anchor* for SI↔Loan links — so we match on the
 * id alone (Convex ids are globally unique). `type` is accepted for call-site
 * readability but isn't needed for the match.
 */
export function linksForRecord(
  links: JobLink[],
  _type: RecordType,
  id: string,
): JobLink[] {
  return links.filter((l) => l.detailId === id || l.anchorId === id);
}

/**
 * Returns the counterpart `{type, id}` for a link, given the id of the record
 * being viewed. Whichever side the viewer's id is NOT on is the counterpart —
 * robust to loans appearing on either side.
 */
export function counterpartOf(
  link: JobLink,
  viewerId: string,
): { type: RecordType; id: string } {
  if (link.detailId === viewerId) {
    return { type: link.anchorType, id: link.anchorId };
  }
  return { type: link.detailType, id: link.detailId };
}

/**
 * Record types that may be linked to the given viewer (and the order their
 * groups appear in the linked-records list / picker). Single source of truth
 * for both the candidate pool and the display grouping.
 *
 * Beyond the standard detail↔anchor model, a Security Interest may link a Loan
 * (the loan it secures) and a Loan may link a Security Interest — the targeted
 * SI↔Loan pairing.
 */
export function linkableTypesFor(viewerType: RecordType): RecordType[] {
  switch (viewerType) {
    case "asset":
    case "creditor":
      return ["security_interest", "loan", "ato_sro"];
    case "security_interest":
      return ["asset", "creditor", "loan"];
    case "loan":
      return ["asset", "creditor", "security_interest"];
    case "ato_sro":
      return ["asset", "creditor"];
  }
}

/**
 * Canonical detail/anchor orientation for a link between two records, used when
 * creating a link from either panel. Assets & creditors are always the anchor;
 * security interests & ATO/SRO are always the detail; a loan is the anchor only
 * when paired with a security interest (an SI secures a loan), the detail
 * otherwise.
 */
export function orientLink(
  a: { type: RecordType; id: string },
  b: { type: RecordType; id: string },
): {
  detailType: DetailType;
  detailId: string;
  anchorType: AnchorType | "loan";
  anchorId: string;
} {
  const isSiLoan =
    (a.type === "security_interest" && b.type === "loan") ||
    (a.type === "loan" && b.type === "security_interest");

  const detail: { type: RecordType; id: string } = isSiLoan
    ? a.type === "security_interest"
      ? a
      : b
    : isAnchorType(a.type)
      ? b
      : a;
  const anchor: { type: RecordType; id: string } = isSiLoan
    ? a.type === "loan"
      ? a
      : b
    : isAnchorType(a.type)
      ? a
      : b;

  return {
    detailType: detail.type as DetailType,
    detailId: detail.id,
    anchorType: anchor.type as AnchorType | "loan",
    anchorId: anchor.id,
  };
}

function prettify(value: string | undefined): string {
  if (!value) return "";
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Record description — turns a raw entry into a RecordRef
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */

function describeSecurityInterest(e: any): RecordRef {
  return {
    type: "security_interest",
    id: e._id,
    title: e.securedParty?.name ?? "Unnamed secured party",
    subtitle: e.ppsrRegistrationNumber
      ? `PPSR #${e.ppsrRegistrationNumber}`
      : "Unperfected",
    amount: typeof e.owedAmount === "number" ? e.owedAmount : undefined,
  };
}

function describeAsset(e: any): RecordRef {
  const selectedIdx = e.selectedValuation?.valuationIndex;
  const selected =
    typeof selectedIdx === "number" ? e.valuations?.[selectedIdx] : undefined;
  return {
    type: "asset",
    id: e._id,
    title: e.description ?? "Asset",
    subtitle: prettify(e.assetType),
    amount: typeof selected?.value === "number" ? selected.value : undefined,
  };
}

function describeCreditor(e: any): RecordRef {
  return {
    type: "creditor",
    id: e._id,
    title: e.creditorName ?? "Creditor",
    subtitle: prettify(e.natureOfDebt),
    amount: typeof e.amount === "number" ? e.amount : undefined,
  };
}

function describeLoan(e: any): RecordRef {
  return {
    type: "loan",
    id: e._id,
    title: e.counterpartyName ?? "Loan",
    subtitle: prettify(e.direction),
    amount:
      typeof e.outstandingBalance === "number"
        ? e.outstandingBalance
        : undefined,
  };
}

function describeAtoSro(e: any): RecordRef {
  return {
    type: "ato_sro",
    id: e._id,
    title: e.authorityLabel ?? "ATO/SRO record",
    subtitle: prettify(e.authority),
    amount:
      typeof e.runningBalance?.amount === "number"
        ? e.runningBalance.amount
        : undefined,
  };
}

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

/** All active record links for a job (polymorphic). */
export function useJobLinks(jobId: Id<"jobs">): JobLink[] {
  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.recordLinks.linkQueries.listLinksForJob,
      { jobId },
    ),
  );
  return data ?? [];
}

/** All pending link suggestions for a job. */
export function useJobLinkSuggestions(jobId: Id<"jobs">): JobLinkSuggestion[] {
  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.recordLinks.linkQueries.listSuggestionsForJob,
      { jobId },
    ),
  );
  return data ?? [];
}

/**
 * Loads every record kind a linking UI may need and indexes them by id.
 * The lists are small (one job's canonical entries) and Convex de-dupes
 * subscriptions, so fetching all five unconditionally is acceptable.
 */
export function useJobRecordIndex(jobId: Id<"jobs">): {
  byId: Map<string, RecordRef>;
  securityInterests: RecordRef[];
  assets: RecordRef[];
  creditors: RecordRef[];
  loans: RecordRef[];
  atoSro: RecordRef[];
} {
  const si = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listSecurityInterestEntriesForJob,
      { jobId },
    ),
  );
  const assets = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAssetEntriesForJob, {
      jobId,
    }),
  );
  const creditors = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listLiabilityEntriesForJob,
      { jobId },
    ),
  );
  const loans = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listLoanEntriesForJob, {
      jobId,
    }),
  );
  const atoSro = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAtoSroEntriesForJob, {
      jobId,
    }),
  );

  const siRefs = ((si.data ?? []) as any[]).map(describeSecurityInterest);
  const assetRefs = ((assets.data ?? []) as any[]).map(describeAsset);
  const creditorRefs = ((creditors.data ?? []) as any[]).map(describeCreditor);
  const loanRefs = ((loans.data ?? []) as any[]).map(describeLoan);
  const atoRefs = ((atoSro.data ?? []) as any[]).map(describeAtoSro);

  const byId = new Map<string, RecordRef>();
  for (const ref of [
    ...siRefs,
    ...assetRefs,
    ...creditorRefs,
    ...loanRefs,
    ...atoRefs,
  ]) {
    byId.set(ref.id, ref);
  }

  return {
    byId,
    securityInterests: siRefs,
    assets: assetRefs,
    creditors: creditorRefs,
    loans: loanRefs,
    atoSro: atoRefs,
  };
}

/**
 * Per-record active-link counts for table indicators. Keys are by record id;
 * the same id space spans every table because Convex Ids are globally unique.
 *
 * - `byDetailId`: links touching a detail record (SI / Loan / ATO-SRO)
 * - `byAnchorId`: links touching an anchor record (Asset / Creditor)
 *
 * Deprecated aliases `bySecurityInterest` / `byLinkedEntity` are kept as
 * proxies onto the new keys so existing call sites still compile while the
 * panels migrate; they cover the SI side of the old SI-centric model and the
 * anchor side respectively. New code should use the explicit keys.
 */
export function useLinkCounts(jobId: Id<"jobs">): {
  byDetailId: Map<string, number>;
  byAnchorId: Map<string, number>;
  /** @deprecated Use `byDetailId` (detail-side counts include SI rows). */
  bySecurityInterest: Map<string, number>;
  /** @deprecated Use `byAnchorId` (anchor-side counts cover assets + creditors). */
  byLinkedEntity: Map<string, number>;
} {
  const links = useJobLinks(jobId);
  const byDetailId = new Map<string, number>();
  const byAnchorId = new Map<string, number>();
  for (const l of links) {
    byDetailId.set(l.detailId, (byDetailId.get(l.detailId) ?? 0) + 1);
    byAnchorId.set(l.anchorId, (byAnchorId.get(l.anchorId) ?? 0) + 1);
  }
  return {
    byDetailId,
    byAnchorId,
    bySecurityInterest: byDetailId,
    byLinkedEntity: byAnchorId,
  };
}

// ---------------------------------------------------------------------------
// Secondary detail stack — multi-panel state helper
// ---------------------------------------------------------------------------

/**
 * One entry in the side-by-side detail panel stack. Each summary panel
 * (Assets, Creditors, Loans, Security Interests, ATO/SRO) opens linked
 * records via this shape and the `SecondaryDetailPanel` dispatches on
 * `type` to render the matching component.
 */
export interface LinkedRecordRef {
  type: RecordType;
  id: string;
}

/**
 * Manages the stack of side-by-side linked-record panels for a parent
 * summary panel. The parent owns the primary panel (a single
 * `selectedEntryId`); this hook owns everything stacked to the left.
 *
 * Behaviour:
 * - Calling `open(type, id)` pushes a new panel to the end of the stack
 *   unless it would duplicate the primary or an already-open secondary.
 * - `closeAt(index)` removes one specific stack entry.
 * - `closeAll()` clears the entire stack (used when the primary panel
 *   itself is closed so nothing is left dangling).
 *
 * The `primary` arg is the `{ type, id }` of the primary panel (or null
 * when nothing is selected) so we can suppress trying to re-open the same
 * record the user is already looking at as the primary.
 */
export function useLinkedRecordStack(primary: LinkedRecordRef | null): {
  stack: LinkedRecordRef[];
  open: (type: RecordType, id: string) => void;
  closeAt: (index: number) => void;
  closeAll: () => void;
} {
  const [stack, setStack] = useState<LinkedRecordRef[]>([]);

  const open = useCallback(
    (type: RecordType, id: string) => {
      // Don't push a duplicate of the primary — the user is already
      // looking at it; opening another copy would be visual noise.
      if (primary && primary.type === type && primary.id === id) return;
      setStack((prev) => {
        if (prev.some((r) => r.type === type && r.id === id)) return prev;
        return [...prev, { type, id }];
      });
    },
    [primary],
  );

  const closeAt = useCallback((index: number) => {
    setStack((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const closeAll = useCallback(() => setStack([]), []);

  return useMemo(
    () => ({ stack, open, closeAt, closeAll }),
    [stack, open, closeAt, closeAll],
  );
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useLinkMutations() {
  const link = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.recordLinks.linkMutations.linkRecord,
    ),
  });
  const unlink = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.recordLinks.linkMutations.unlinkRecord,
    ),
  });
  const reorder = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.recordLinks.linkMutations.reorderLinks,
    ),
  });
  const accept = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.recordLinks.linkMutations.acceptLinkSuggestion,
    ),
  });
  const dismiss = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.recordLinks.linkMutations.dismissLinkSuggestion,
    ),
  });
  return { link, unlink, reorder, accept, dismiss };
}

/* eslint-enable @typescript-eslint/no-explicit-any */
